/**
 * Toast Notification Utilities
 *
 * Production-grade toast notification system using Sonner for non-blocking feedback.
 * Replaces modal dialogs with elegant, auto-dismissing toasts.
 *
 * @module lib/toast
 *
 * @example
 * ```typescript
 * import { showJobStartToast, showJobCompleteToast } from '@/lib/toast';
 *
 * // When starting a job
 * const jobId = await startJob(projectId, researchOverview);
 * showJobStartToast(jobId, 'Healthcare Tech', 5);
 *
 * // When job completes
 * showJobCompleteToast(reportId, 'Healthcare Tech', {
 *   marketSize: '$47B',
 *   growthRate: '23% CAGR',
 *   targetCount: 87
 * });
 * ```
 */

import { toast, ExternalToast } from 'sonner';
import { createElement } from 'react';
import {
  JobStartToast,
  JobCompleteToast,
  JobErrorToast,
  JobProgressToast
} from '../components/notifications/JobToasts';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Report metrics displayed in completion toast
 */
export interface ReportMetrics {
  /** Market size (e.g., "$47B") */
  marketSize: string;
  /** Growth rate (e.g., "23% CAGR") */
  growthRate: string;
  /** Number of targets identified */
  targetCount?: number;
  /** Optional key insight or headline */
  keyInsight?: string;
  /** Optional additional metrics */
  [key: string]: string | number | undefined;
}

/**
 * Error details for error toasts
 */
export interface JobError {
  /** Error code (e.g., 'network-error', 'invalid-sector') */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Whether the operation can be retried */
  retryable: boolean;
}

/**
 * Job progress details for progress toasts
 */
export interface JobProgress {
  /** Current progress percentage (0-100) */
  percent: number;
  /** Status message (e.g., "Identifying targets...") */
  message: string;
  /** Estimated time remaining in seconds */
  estimatedSeconds?: number;
}

// ============================================================================
// TOAST CONFIGURATION
// ============================================================================

/**
 * Default toast configuration for job start notifications
 */
const JOB_START_CONFIG: ExternalToast = {
  duration: 4000, // 4 seconds
  position: 'bottom-right',
  dismissible: true,
  closeButton: false,
};

/**
 * Default toast configuration for job completion notifications
 */
const JOB_COMPLETE_CONFIG: ExternalToast = {
  duration: 12000, // 12 seconds
  position: 'bottom-right',
  dismissible: true,
  closeButton: true,
};

/**
 * Default toast configuration for error notifications
 * Note: Errors do NOT auto-dismiss
 */
const JOB_ERROR_CONFIG: ExternalToast = {
  duration: Infinity, // Persistent - user must dismiss
  position: 'bottom-right',
  dismissible: true,
  closeButton: true,
};

/**
 * Default toast configuration for progress updates
 */
const JOB_PROGRESS_CONFIG: ExternalToast = {
  duration: Infinity, // Stays visible until job completes
  position: 'bottom-right',
  dismissible: false, // Can't dismiss while job is running
  closeButton: false,
};

// ============================================================================
// TOAST FUNCTIONS
// ============================================================================

/**
 * Shows a toast notification when a job starts
 *
 * Displays a brief, auto-dismissing notification confirming the job
 * has been queued and providing an estimated completion time.
 *
 * @param jobId - Unique identifier for the job
 * @param sector - Sector name (e.g., "Healthcare Tech")
 * @param estimatedMinutes - Estimated time to completion in minutes
 * @param onViewStatus - Optional callback when "View Status" is clicked
 * @returns Toast ID for programmatic dismissal
 *
 * @example
 * ```typescript
 * const toastId = showJobStartToast('job-123', 'Healthcare Tech', 5, () => {
 *   navigate('/project/abc/job/job-123');
 * });
 * ```
 */
export function showJobStartToast(
  jobId: string,
  sector: string,
  estimatedMinutes: number,
  onViewStatus?: () => void
): string | number {
  return toast.custom(
    (t: string | number) =>
      createElement(JobStartToast, {
        jobId,
        sector,
        estimatedMinutes,
        onViewStatus,
        onDismiss: () => toast.dismiss(t),
      }),
    JOB_START_CONFIG
  );
}

/**
 * Shows a toast notification when a job completes successfully
 *
 * Displays a celebration toast with key metrics from the report,
 * providing quick preview and full report access.
 *
 * @param reportId - Unique identifier for the completed report
 * @param sector - Sector name (e.g., "Healthcare Tech")
 * @param metrics - Key metrics to display
 * @param onQuickPreview - Callback when "Quick Preview" is clicked
 * @param onViewReport - Callback when "View Report" is clicked
 * @returns Toast ID for programmatic dismissal
 *
 * @example
 * ```typescript
 * showJobCompleteToast('report-456', 'Healthcare Tech', {
 *   marketSize: '$47B',
 *   growthRate: '23% CAGR',
 *   targetCount: 87,
 *   keyInsight: 'Strong consolidation opportunity'
 * }, handlePreview, handleViewReport);
 * ```
 */
export function showJobCompleteToast(
  reportId: string,
  sector: string,
  metrics: ReportMetrics,
  onQuickPreview?: () => void,
  onViewReport?: () => void
): string | number {
  return toast.custom(
    (t: string | number) =>
      createElement(JobCompleteToast, {
        reportId,
        sector,
        metrics,
        onQuickPreview,
        onViewReport,
        onDismiss: () => toast.dismiss(t),
      }),
    JOB_COMPLETE_CONFIG
  );
}

/**
 * Shows a toast notification when a job fails
 *
 * Displays a persistent error toast with the error message and
 * contextual actions (Retry, Edit Query, etc.) based on error type.
 *
 * NOTE: Error toasts do NOT auto-dismiss - user must explicitly dismiss.
 *
 * @param jobId - Unique identifier for the failed job
 * @param sector - Sector name (e.g., "Healthcare Tech")
 * @param error - Error details
 * @param onRetry - Optional callback when "Retry" is clicked
 * @param onEditQuery - Optional callback when "Edit Query" is clicked
 * @returns Toast ID for programmatic dismissal
 *
 * @example
 * ```typescript
 * showJobErrorToast('job-789', 'Healthcare Tech', {
 *   code: 'insufficient-data',
 *   message: 'Insufficient data for sector',
 *   retryable: true
 * }, handleRetry, handleEdit);
 * ```
 */
export function showJobErrorToast(
  jobId: string,
  sector: string,
  error: JobError,
  onRetry?: () => void,
  onEditQuery?: () => void
): string | number {
  return toast.custom(
    (t: string | number) =>
      createElement(JobErrorToast, {
        jobId,
        sector,
        error,
        onRetry,
        onEditQuery,
        onDismiss: () => toast.dismiss(t),
      }),
    JOB_ERROR_CONFIG
  );
}

/**
 * Shows a toast notification for job progress updates
 *
 * Displays a persistent progress toast that updates as the job progresses.
 * Use updateJobProgressToast() to update the same toast instance.
 *
 * @param jobId - Unique identifier for the job
 * @param sector - Sector name (e.g., "Healthcare Tech")
 * @param progress - Current progress details
 * @param onCancel - Optional callback when "Cancel" is clicked
 * @returns Toast ID for programmatic updates/dismissal
 *
 * @example
 * ```typescript
 * const toastId = showJobProgressToast('job-123', 'Healthcare Tech', {
 *   percent: 25,
 *   message: 'Identifying targets...',
 *   estimatedSeconds: 180
 * }, handleCancel);
 *
 * // Later, update the same toast
 * updateJobProgressToast(toastId, {
 *   percent: 50,
 *   message: 'Researching companies...',
 *   estimatedSeconds: 90
 * });
 * ```
 */
export function showJobProgressToast(
  jobId: string,
  sector: string,
  progress: JobProgress,
  onCancel?: () => void
): string | number {
  return toast.custom(
    (t: string | number) =>
      createElement(JobProgressToast, {
        jobId,
        sector,
        progress,
        onCancel,
        onDismiss: () => toast.dismiss(t),
      }),
    JOB_PROGRESS_CONFIG
  );
}

/**
 * Updates an existing job progress toast
 *
 * @param toastId - Toast ID returned from showJobProgressToast()
 * @param _progress - Updated progress details (currently unused - for future API)
 *
 * @example
 * ```typescript
 * const toastId = showJobProgressToast('job-123', 'Healthcare Tech', {...});
 *
 * // Update progress
 * updateJobProgressToast(toastId, {
 *   percent: 75,
 *   message: 'Analyzing trends...',
 *   estimatedSeconds: 30
 * });
 * ```
 */
export function updateJobProgressToast(
  toastId: string | number,
  _progress: JobProgress
): void {
  // Sonner doesn't have a native update API, so we dismiss and recreate
  // In practice, the JobProgressToast component should handle updates internally
  // via props changes. This is here for API completeness.
  toast.dismiss(toastId);
}

/**
 * Dismisses a specific toast by ID
 *
 * @param toastId - Toast ID to dismiss
 *
 * @example
 * ```typescript
 * const toastId = showJobStartToast('job-123', 'Healthcare Tech', 5);
 *
 * // Dismiss immediately
 * dismissToast(toastId);
 * ```
 */
export function dismissToast(toastId: string | number): void {
  toast.dismiss(toastId);
}

/**
 * Dismisses all active toasts
 *
 * Useful when navigating away from a page or cleaning up on unmount.
 *
 * @example
 * ```typescript
 * useEffect(() => {
 *   return () => {
 *     dismissAllToasts();
 *   };
 * }, []);
 * ```
 */
export function dismissAllToasts(): void {
  toast.dismiss();
}

// ============================================================================
// ERROR TYPE HELPERS
// ============================================================================

/**
 * Maps error codes to user-friendly messages and actions
 *
 * @param errorCode - Error code from backend
 * @returns Contextual error details
 *
 * @internal
 */
export function getErrorContext(errorCode: string): {
  message: string;
  retryable: boolean;
  action: 'retry' | 'edit' | 'wait' | 'dismiss';
  actionLabel: string;
} {
  switch (errorCode) {
    case 'network-error':
    case 'connection-lost':
      return {
        message: 'Connection lost. Please check your internet connection.',
        retryable: true,
        action: 'retry',
        actionLabel: 'Retry',
      };

    case 'invalid-sector':
    case 'sector-not-found':
      return {
        message: 'Unable to identify sector. Please refine your query.',
        retryable: true,
        action: 'edit',
        actionLabel: 'Edit Query',
      };

    case 'rate-limit':
    case 'too-many-requests':
      return {
        message: 'Too many requests. Try again in 2 minutes.',
        retryable: false,
        action: 'wait',
        actionLabel: 'OK',
      };

    case 'timeout':
    case 'generation-timeout':
      return {
        message: 'Generation taking longer than expected.',
        retryable: true,
        action: 'retry',
        actionLabel: 'Continue Waiting',
      };

    case 'insufficient-data':
      return {
        message: 'Insufficient data available for this sector.',
        retryable: true,
        action: 'edit',
        actionLabel: 'Edit Query',
      };

    case 'authentication-error':
      return {
        message: 'Session expired. Please log in again.',
        retryable: false,
        action: 'dismiss',
        actionLabel: 'OK',
      };

    default:
      return {
        message: 'An unexpected error occurred. Please try again.',
        retryable: true,
        action: 'retry',
        actionLabel: 'Retry',
      };
  }
}

/**
 * Formats a duration in seconds to human-readable string
 *
 * @param seconds - Duration in seconds
 * @returns Formatted string (e.g., "5 min", "90 sec")
 *
 * @internal
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)} sec`;
  } else if (seconds < 3600) {
    const minutes = Math.round(seconds / 60);
    return `${minutes} min`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
}

/**
 * Formats a large number to compact string (e.g., 87 -> "87", 1234 -> "1.2K")
 *
 * @param num - Number to format
 * @returns Formatted string
 *
 * @internal
 */
export function formatCompactNumber(num: number): string {
  if (num < 1000) return num.toString();
  if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
  if (num < 1000000000) return `${(num / 1000000).toFixed(1)}M`;
  return `${(num / 1000000000).toFixed(1)}B`;
}
